/**
 * Line states on the map and in Planning (MB decision 372, Q315; sign-off 04-031, 05-014; split as the lines-remaining
 * map by decision 605, Q532): seven states worked out from Line review automatically, with the crew's flown or unflown
 * mark on top. Whether a reflight or patch is planned makes a state of its own, in the lines-remaining map's colour
 * and words, so every map of both apps names and colours a line the same way.
 *
 * | State | From Line review (`progress.ts` `LineState`) | Drawn |
 * |---|---|---|
 * | Unflown | no status yet | `map-line-unflown`, dashed (the planned line colour) |
 * | Flown, awaiting QC | flown, review pending | `map-line-awaiting-qc` (solid light blue, decision 379) |
 * | Patch planned | part accepted, its patch planned | the flown part `map-line-completed`, the patch `map-line-to-fly` (pink, thick) |
 * | Part accepted, no patch planned | part accepted, no patch | `map-line-part-accepted` (purple), the part the log recorded `map-line-completed` |
 * | Reflight planned | rejected, its reflight planned | `map-line-reflight` (yellow) |
 * | Rejected, no reflight planned | rejected, no reflight | `map-line-rejected` (red) |
 * | Completed | accepted (the line, a full reflight or its last patch) | `map-line-completed`, thin (decision 654) |
 *
 * The crew's mark (`line_flown_override`, item 04-031) changes only flown or unflown: "flown" on an unflown or
 * rejected line (its reflight planned or not) makes it "Flown, awaiting QC" until the flight is imported and reviewed;
 * "not flown" makes any line "Unflown". Colours are map tokens of their own, never the tenant's brand (decision 352),
 * and every colour goes with its word in the legend and the tooltip. Since decision 659 every state is the
 * lines-remaining map's legend entry itself: one set of words, colours and widths on every map. Every line has the
 * thin dark edge under it (decision 654). A patch still to fly and a planned reflight name the way they are planned to
 * be flown (`direction_deg`, the Refly list's: the way the original was flown, decision 1013), and with `arrows` carry
 * arrows that way (decision 1011).
 */
import { formatKm } from './km.js';
import type { Block, LineStatus, PlannedLine, Project } from './model.js';
import {
  blockMap,
  plannedLinePoints,
  plannedSegmentPoints,
  type LayerResult,
  type LegendEntry,
  type MapColour,
  type MapFeature,
  type MapStroke,
} from './map-layers.js';
import { REMAINING_LEGEND } from './lines-remaining.js';
import {
  alongDirection,
  directionArrowFeatures,
  plannedDirection,
  type PlannedDirection,
} from './direction-arrows.js';
import { planProgress, type LineState } from './progress.js';
import { plannedLineRole } from './reflights.js';
import { parseLineNumber } from './lines.js';
import { flownLineKey } from './planning.js';
import type { FlownMark } from './survey-lines.js';

export type SurveyLineState =
  | 'unflown'
  | 'awaiting_qc'
  | 'patch_planned'
  | 'part_accepted'
  | 'reflight_planned'
  | 'rejected'
  | 'completed';

/** How a state is drawn: its colour token, stroke and dash (patch planned: the patch). */
export interface LineStateStyle {
  readonly colour: MapColour;
  readonly stroke: MapStroke;
  readonly dashed: boolean;
}

const styleOf = (e: LegendEntry): LineStateStyle => ({
  colour: e.colour,
  stroke: e.stroke,
  dashed: e.dashed,
});

/**
 * Every state is the lines-remaining map's legend entry (decisions 605 and 659): the same colour, stroke and words on
 * every map, so no legend says one thing two ways.
 */
const SHARED: Readonly<Record<SurveyLineState, LegendEntry>> = {
  unflown: REMAINING_LEGEND.not_flown,
  awaiting_qc: REMAINING_LEGEND.awaiting_review,
  patch_planned: REMAINING_LEGEND.patch_planned,
  part_accepted: REMAINING_LEGEND.partial,
  reflight_planned: REMAINING_LEGEND.reflight_planned,
  rejected: REMAINING_LEGEND.rejected,
  completed: REMAINING_LEGEND.accepted,
};

export const LINE_STATE_STYLE: Readonly<Record<SurveyLineState, LineStateStyle>> = {
  unflown: styleOf(SHARED.unflown),
  awaiting_qc: styleOf(SHARED.awaiting_qc),
  patch_planned: styleOf(SHARED.patch_planned),
  part_accepted: styleOf(SHARED.part_accepted),
  reflight_planned: styleOf(SHARED.reflight_planned),
  rejected: styleOf(SHARED.rejected),
  completed: styleOf(SHARED.completed),
};

export const LINE_STATE_WORDS: Readonly<Record<SurveyLineState, string>> = {
  unflown: SHARED.unflown.word,
  awaiting_qc: SHARED.awaiting_qc.word,
  patch_planned: SHARED.patch_planned.word,
  part_accepted: SHARED.part_accepted.word,
  reflight_planned: SHARED.reflight_planned.word,
  rejected: SHARED.rejected.word,
  completed: SHARED.completed.word,
};

/** The colour token of a state (patch planned: the patch still to fly). */
export const LINE_STATE_COLOUR: Readonly<Record<SurveyLineState, MapColour>> = {
  unflown: LINE_STATE_STYLE.unflown.colour,
  awaiting_qc: LINE_STATE_STYLE.awaiting_qc.colour,
  patch_planned: LINE_STATE_STYLE.patch_planned.colour,
  part_accepted: LINE_STATE_STYLE.part_accepted.colour,
  reflight_planned: LINE_STATE_STYLE.reflight_planned.colour,
  rejected: LINE_STATE_STYLE.rejected.colour,
  completed: LINE_STATE_STYLE.completed.colour,
};

const entry = (s: SurveyLineState): LegendEntry => ({ ...LINE_STATE_STYLE[s], word: LINE_STATE_WORDS[s] });

/** The flown part of a part-flown line (its patch planned, or part accepted with the part the log recorded): thin. */
export const FLOWN_PART_LEGEND: LegendEntry = {
  colour: 'map-line-completed',
  stroke: 'thin',
  dashed: false,
  word: 'Part flown: flown part',
};

/** The legend of the line states, in order: the flown part of a part-flown line before its patch. */
export const LINE_STATE_LEGEND: readonly LegendEntry[] = [
  entry('unflown'),
  entry('awaiting_qc'),
  FLOWN_PART_LEGEND,
  entry('patch_planned'),
  entry('part_accepted'),
  entry('reflight_planned'),
  entry('rejected'),
  entry('completed'),
];

/** The state of a line from where Line review has it, with the crew's flown (true) or not flown (false) mark. */
export function lineStateOf(progress: LineState, crewFlown?: boolean): SurveyLineState {
  const reviewed: SurveyLineState =
    progress === 'not_flown'
      ? 'unflown'
      : progress === 'awaiting_review'
        ? 'awaiting_qc'
        : progress === 'accepted'
          ? 'completed'
          : progress === 'partial'
            ? 'part_accepted'
            : progress;
  if (crewFlown === false) return 'unflown';
  if (
    crewFlown === true &&
    (reviewed === 'unflown' || reviewed === 'rejected' || reviewed === 'reflight_planned')
  )
    return 'awaiting_qc';
  return reviewed;
}

/** The state of each original planned line by its number. `crew` gives a line's crew mark, if any. */
export function lineStatesByNumber(
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  crew: (line: PlannedLine) => boolean | undefined = () => undefined,
): Map<number, SurveyLineState> {
  const out = new Map<number, SurveyLineState>();
  for (const lp of planProgress(statuses, plan).lines) {
    out.set(lp.original.number, lineStateOf(lp.state, crew(lp.original)));
  }
  return out;
}

const km = (m: number): string => `${formatKm(m / 1000)} km`;

/**
 * The line states layer of the map (the "Line states" layer, `flown`): every original planned line in its state. A
 * line with its patch planned is drawn green (flown) with the patch pink and thick over it; a part accepted line with
 * no patch purple, the part the log recorded (`start_along_m`, `end_along_m`) green over it; a rejected line yellow
 * when its reflight is planned (the tooltip names the reflight), red when not. `arrows` adds the direction arrows of
 * the patches still to fly and the planned reflights (decision 1011).
 */
export function lineStateFeatures(
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  blocks: readonly Block[],
  project: Pick<Project, 'epsg'>,
  crew: (line: PlannedLine) => boolean | undefined = () => undefined,
  options: { readonly arrows?: boolean } = {},
): LayerResult {
  const bm = blockMap(blocks);
  const features: MapFeature[] = [];
  let missing = 0;
  const live = statuses.filter((s) => s.deleted_at === null);
  const push = (
    id: string,
    points: MapFeature['geometry'],
    look: LineStateStyle,
    tooltip: string,
    identify: MapFeature['identify'],
    direction?: PlannedDirection,
  ): void => {
    features.push({
      id,
      layer: 'flown',
      geometry: points,
      style: { colour: look.colour, stroke: look.stroke, dashed: look.dashed, fill: 0, casing: true },
      tooltip,
      identify,
      ...(direction ? { direction_deg: direction.deg, direction_from: direction.from } : {}),
    });
  };
  const flownPart = styleOf(FLOWN_PART_LEGEND);
  for (const lp of planProgress(statuses, plan).lines) {
    const o = lp.original;
    const pts = plannedLinePoints(o, bm, project);
    if (!pts) {
      missing += 1;
      continue;
    }
    const state = lineStateOf(lp.state, crew(o));
    const word = LINE_STATE_WORDS[state];
    const open = lp.open;
    const reflight = state === 'reflight_planned' && open !== null ? String(open.number) : null;
    const base = [
      { label: 'Line', value: String(o.number) },
      { label: 'Kind', value: o.kind === 'tie' ? 'Tie' : 'Traverse' },
      { label: 'State', value: word },
      ...(reflight ? [{ label: 'Reflight', value: reflight }] : []),
      { label: 'Planned', value: km(o.length_m) },
      { label: 'Still to fly', value: km(state === 'completed' ? 0 : lp.remaining_m) },
    ];
    const line = { type: 'line' as const, points: pts };
    if (state === 'patch_planned') {
      const patch =
        open !== null &&
        plannedLineRole(open) === 'patch' &&
        open.segment_from_m != null &&
        open.segment_to_m != null
          ? plannedSegmentPoints(o, open.segment_from_m, open.segment_to_m, bm, project)
          : null;
      if (patch) {
        // flown outside the patch, the patch still to fly, its points the way it is to be flown (decision 1013)
        const dir = plannedDirection(o, open, live);
        push(o.id, line, flownPart, `${o.number} · ${FLOWN_PART_LEGEND.word}`, base);
        push(
          `${o.id}:to-fly`,
          { type: 'line', points: alongDirection(patch, dir)! },
          LINE_STATE_STYLE.patch_planned,
          `${o.number} · ${word} (patch ${open!.number}, ${km(open!.length_m)})`,
          base,
          dir,
        );
        continue;
      }
    }
    // a planned reflight's points run the way it is to be flown (decision 1013)
    const dir = reflight ? plannedDirection(o, open, live) : undefined;
    push(
      o.id,
      dir ? { type: 'line', points: alongDirection(pts, dir)! } : line,
      LINE_STATE_STYLE[state],
      `${o.number} · ${word}${reflight ? ` (${reflight} reflight)` : ''}`,
      base,
      dir,
    );
    if (state !== 'part_accepted') continue;
    const logged = live.find(
      (s) =>
        (s.line_number === o.number || s.planned_line_id === o.id) &&
        s.start_along_m != null &&
        s.end_along_m != null,
    );
    const flown = logged
      ? plannedSegmentPoints(o, logged.start_along_m!, logged.end_along_m!, bm, project)
      : null;
    if (flown) {
      push(
        `${o.id}:flown`,
        { type: 'line', points: flown },
        flownPart,
        `${o.number} · ${FLOWN_PART_LEGEND.word}`,
        base,
      );
    }
  }
  if (options.arrows) features.push(...directionArrowFeatures(features));
  return {
    features,
    notDrawn: missing > 0 ? [`${missing} planned line(s): no map projection for their block`] : [],
  };
}

/**
 * The crew's mark of each planned line (`line_flown_override`, item 04-031), the latest change of a key winning: a
 * function for `lineStateFeatures` and `lineStatesByNumber`. Keys carry the project number, the block digit, the line
 * number and kind, as `flownLineKey` builds them for the Planning screen.
 */
export function crewMarks(
  marks: readonly FlownMark[],
  projectNumber: string,
  blocks: readonly Block[],
): (line: PlannedLine) => boolean | undefined {
  const latest = new Map<string, boolean>();
  const ordered = [...marks].sort((a, b) =>
    a.updated_at < b.updated_at ? -1 : a.updated_at > b.updated_at ? 1 : 0,
  );
  for (const m of ordered) latest.set(m.flown_key, m.flown);
  const digit = new Map(blocks.map((b) => [b.id, b.digit]));
  return (line) => {
    if (latest.size === 0) return undefined;
    const parsed = parseLineNumber(line.number);
    if (!parsed.production) return undefined;
    return latest.get(
      flownLineKey({
        project_number: projectNumber,
        block_num: Number(digit.get(line.block_id) ?? parsed.blockDigit),
        line_num: parsed.number,
        line_type: parsed.kind === 'tie' ? 'tie' : 'trav',
      }),
    );
  };
}

/**
 * The state of each line of the project by its flown key (`flownLineKey`, project number included), for the lines
 * of the `.lns` files on the Planning screen: the line state of the planned line with the same key, the crew's mark on
 * top.
 */
export function lineStatesByKey(
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  blocks: readonly Block[],
  projectNumber: string,
  crew: (line: PlannedLine) => boolean | undefined = () => undefined,
): Map<string, SurveyLineState> {
  const digit = new Map(blocks.map((b) => [b.id, b.digit]));
  const out = new Map<string, SurveyLineState>();
  for (const lp of planProgress(statuses, plan).lines) {
    const o = lp.original;
    const parsed = parseLineNumber(o.number);
    if (!parsed.production) continue;
    out.set(
      flownLineKey({
        project_number: projectNumber,
        block_num: Number(digit.get(o.block_id) ?? parsed.blockDigit),
        line_num: parsed.number,
        line_type: parsed.kind === 'tie' ? 'tie' : 'trav',
      }),
      lineStateOf(lp.state, crew(o)),
    );
  }
  return out;
}
